import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { RotateCw } from "lucide-react";

import { Button, IconButton } from "@/components/ui/button";
import { cn } from "@/utils/utils";
import { useTranslation } from "@/store/locale";

/**
 * Whether any query under `queryKeys` is in flight — the match the button spins
 * on, exported so a caller can say so somewhere less peripheral than a 14px
 * icon. A key matches by prefix, so `["/clubs"]` covers `["/clubs", "infinite"]`.
 */
export function useFetchingKeys(queryKeys: readonly (readonly unknown[])[]): boolean {
  return (
    useIsFetching({
      predicate: (query) =>
        queryKeys.some((key) =>
          key.every((part, index) => Object.is(query.queryKey[index], part)),
        ),
    }) > 0
  );
}

/**
 * Refetches every query under any of `queryKeys`; spins while one is in flight.
 *
 * Labelled by default: where it sits beside a worded "Add …" action, a lone
 * glyph next to a written one reads as decoration. Ghost either way, because
 * it is housekeeping — the action beside it is the one the screen is for.
 *
 * The label stays put while fetching and the icon turns instead: swapping the
 * word for a spinner would make the button change width mid-press.
 */
export function RefreshButton({
  queryKeys,
  iconOnly = false,
  className,
}: {
  /** Readonly, so a key written `as const` is a key this accepts. */
  queryKeys: readonly (readonly unknown[])[];
  /**
   * Drop the word and keep the glyph.
   *
   * For a header that already carries a count and a worded Add: three pieces of
   * text in a row, one of which is housekeeping, and the housekeeping one draws
   * the eye it does not deserve. The name stays on the button for anyone who
   * cannot see the glyph, and as its tooltip.
   */
  iconOnly?: boolean;
  className?: string;
}) {
  const t = useTranslation();
  const queryClient = useQueryClient();
  const fetching = useFetchingKeys(queryKeys);
  const refetch = () => {
    for (const queryKey of queryKeys) void queryClient.invalidateQueries({ queryKey });
  };
  const spinner = <RotateCw className={cn(fetching && "animate-spin")} aria-hidden />;
  if (iconOnly) {
    return (
      <IconButton
        variant="ghost"
        label={t("rp.refresh")}
        aria-busy={fetching || undefined}
        onClick={refetch}
        className={className}
      >
        {spinner}
      </IconButton>
    );
  }
  return (
    <Button
      variant="ghost"
      size="sm"
      aria-busy={fetching || undefined}
      onClick={refetch}
      className={className}
    >
      {spinner}
      {t("rp.refresh")}
    </Button>
  );
}
